const REVIEW_LINE_PARAM = "review_line";
const RETURN_TO_PARAM = "return_to";
const REVIEWABLE_STATUSES = new Set(["open", "in_review", "resolved"]);

export type BelegReviewUrlTarget =
  | { kind: "none"; returnTo: string | null }
  | { kind: "invalid"; returnTo: string | null }
  | { kind: "line"; linePos: string; returnTo: string | null };

export function belegReviewHref({
  belegId,
  linePos,
  priceAnomalyStatus,
  returnTo,
}: {
  belegId: string | null | undefined;
  linePos: string | number | null | undefined;
  priceAnomalyStatus: "open" | "in_review" | "resolved" | null | undefined;
  returnTo: string;
}): string | null {
  const normalizedBelegId = belegId?.trim() ?? "";
  const normalizedLinePos = normalizeLinePos(linePos);
  if (
    !normalizedBelegId ||
    normalizedLinePos === null ||
    !priceAnomalyStatus ||
    !REVIEWABLE_STATUSES.has(priceAnomalyStatus)
  ) {
    return null;
  }

  const params = new URLSearchParams({
    [REVIEW_LINE_PARAM]: normalizedLinePos,
  });
  if (isSafeInternalPath(returnTo)) {
    params.set(RETURN_TO_PARAM, returnTo);
  }
  return `/belege/${encodeURIComponent(normalizedBelegId)}?${params.toString()}`;
}

export function parseBelegReviewUrl(search: string): BelegReviewUrlTarget {
  const params = new URLSearchParams(search);
  const returnTo = safeReturnTarget(params.get(RETURN_TO_PARAM));
  if (!params.has(REVIEW_LINE_PARAM)) return { kind: "none", returnTo };

  const linePos = normalizeLinePos(params.get(REVIEW_LINE_PARAM));
  if (linePos === null) return { kind: "invalid", returnTo };
  return { kind: "line", linePos, returnTo };
}

function normalizeLinePos(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  if (
    normalized.length === 0 ||
    normalized.length > 100 ||
    /[\u0000-\u001f\u007f]/.test(normalized)
  ) {
    return null;
  }
  return normalized;
}

function safeReturnTarget(value: string | null): string | null {
  return value && isSafeInternalPath(value) ? value : null;
}

function isSafeInternalPath(value: string): boolean {
  return (
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !/[\\\u0000-\u001f\u007f]/.test(value)
  );
}
